import { v4 as uuidv4 } from 'uuid';

import appSettings from 'wanderu-config';
import Logger from 'wanderu-logger';

import { experimentEligibilityReceived } from './experimentEligibilitySlice';
import { variantsReceived } from './variantsSlice';

import type { RemoteEvaluationClient } from '@amplitude/experiment-node-server';
import type { Request, Response } from 'express';
import type { Cookies } from 'react-cookie';
import type { Dispatch } from 'redux';

const logger = Logger.getLogger('wanderu-analytics');
const CF_BOT_SCORE = 5;

let experiment: RemoteEvaluationClient;

type IneligibilityReason = string | null;
interface UserProperties {
    accept?: string;
    acceptLanguage?: string;
    asn?: string;
    cfBotScore?: number;
    ipAddress?: string;
    ja4?: string;
    userAgent?: string;
}
type EligibilityReturnValue = [boolean, IneligibilityReason, UserProperties];

export function getIsEligibleForAssignment(req: Request): EligibilityReturnValue {
    const asn = String(req?.headers?.['x-visitor-asn']);
    const cfBotScore = Number(req?.headers?.['cf-bot-score']);
    const ipAddress = String(req?.headers?.['x-real-ip']);
    const ja4 = String(req?.headers?.['cf-ja4']);
    const userAgent = req?.headers?.['user-agent'] || '';
    const acceptLanguage = req?.headers?.['accept-language'];
    const accept = req?.headers?.accept;

    const userProperties = {
        accept,
        acceptLanguage,
        asn,
        cfBotScore,
        ipAddress,
        ja4,
        userAgent,
    };

    // Check Cloudflare bot score - 0 is falsey which is fine because that means "not computed" by cloudflare
    const shouldBlockBotScore = cfBotScore && cfBotScore < Number(CF_BOT_SCORE);
    const shouldBlockASN = asn
        && [15169, 199661, 36116, 8075, 714, 13238, 13414].includes(Number(asn));
    const shouldBlockUserAgent = userAgent?.includes('Google-Travel');

    if (shouldBlockBotScore || shouldBlockASN || shouldBlockUserAgent) {
        let label = 'Cloudflare bot score';
        if (shouldBlockASN) label = 'ASN';
        if (shouldBlockUserAgent) label = 'Google Travel user agent';

        return [false, label, userProperties];
    }

    // Check for missing/suspicious headers
    if (!userAgent || !acceptLanguage) {
        return [false, 'missing/suspicious headers', userProperties];
    }

    // Missing critical browser headers
    if (!accept || !accept.includes('text/html')) {
        return [false, 'missing critical browser headers', userProperties];
    }

    return [true, null, userProperties];
}

interface FetchServerVariantsParams {
    cookies: Cookies;
    dispatch: Dispatch;
    req: Request;
    res: Response;
}

// eslint-disable-next-line sort-exports/sort-exports
export async function fetchServerVariants({
    cookies,
    dispatch,
    req,
    res,
}: FetchServerVariantsParams) {
    const apiKey = appSettings?.config?.amplitude?.apiKey;
    const isServerSide = typeof window === 'undefined';

    if (!apiKey || !isServerSide) {
        return;
    }

    const deviceId = await handleAmplitudeCookies(cookies, res);

    const [
        isEligibleForAssignment,
        ineligibilityReason,
        userProperties,
    ] = getIsEligibleForAssignment(req);

    logger.event('isEligibleForAssignment', {
        deviceId,
        ineligibilityReason,
        isEligibleForAssignment,
        ...userProperties,
    });

    const experimentEligibility = {
        deviceId,
        ineligibilityReason,
        isEligibleForAssignment,
        userProperties,
    };

    dispatch(experimentEligibilityReceived({ experimentEligibility }));

    if (!isEligibleForAssignment) {
        return;
    }

    if (!experiment) {
        const config = {
            fetchRetries: 1,
            fetchRetryBackoffMinMillis: 0,
            fetchRetryTimeoutMillis: 500,
            fetchTimeoutMillis: 500,
        };

        // eslint-disable-next-line import/no-extraneous-dependencies, global-require, @typescript-eslint/no-require-imports
        experiment = require('@amplitude/experiment-node-server')
            .Experiment
            .initializeRemote(apiKey, config);
    }

    const variants = await experiment
        .fetchV2({ device_id: deviceId });

    dispatch(variantsReceived({ variants }));
}

async function handleAmplitudeCookies(cookies: Cookies, res: Response) { // https://amplitude.com/docs/sdks/experiment-sdks/experiment-node-js#access-amplitude-cookies
    let deviceId: string | undefined;

    const apiKey = appSettings?.config?.amplitude?.apiKey;

    // eslint-disable-next-line import/no-extraneous-dependencies, global-require, @typescript-eslint/no-require-imports
    const { AmplitudeCookie } = require('@amplitude/experiment-node-server');

    // Get the cookie name for the Amplitude API key
    const ampCookieName = AmplitudeCookie.cookieName(apiKey, true);

    const cookie = cookies.get(ampCookieName);
    // Try to get device ID from existing cookie
    if (cookie) {
        deviceId = AmplitudeCookie.parse(cookie, true).device_id;
    }

    // If no device ID found, generate a new one and set the cookie
    if (!deviceId) {
        deviceId = uuidv4();

        const ampCookieValue = AmplitudeCookie.generate(deviceId, true);

        cookies.set(ampCookieName, ampCookieValue);
        res.cookie(ampCookieName, ampCookieValue, {
            domain: '.wanderu.com', // this should be the same domain used by the Amplitude JS SDK
            httpOnly: false,
            secure: false,
        });
    }

    return deviceId;
}
